/**
 * Предел длительности одной живой записи: арифметика и тексты.
 *
 * Число задаёт СЕРВЕР (`bit-streaming`, `max_session_sec`) и присылает в кадре
 * `status` при старте сессии. Своей копии у фронта нет намеренно: до 19.09.2026
 * потолок жил только в конфиге сервиса, фронт знал о нём лишь постфактум — из
 * текста «2 часа», вшитого в сообщение о закрытии, — и предупредить заранее не
 * мог. Запись вставала молча, и человек узнавал об этом через час
 * (`docs/backlog/streaming-max-session-silent-stop.md`).
 */

/** За сколько до предела предупреждаем. */
export const WARN_BEFORE_SEC = 600;

const HOUR_FORMS = ['час', 'часа', 'часов'];
const MINUTE_FORMS = ['минуту', 'минуты', 'минут'];

/** Русское склонение по числу: 1 час, 2 часа, 5 часов. */
function plural(n, forms) {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return forms[2];
  const mod10 = n % 10;
  if (mod10 === 1) return forms[0];
  if (mod10 >= 2 && mod10 <= 4) return forms[1];
  return forms[2];
}

/**
 * Предел словами: 10800 → «3 часа», 5400 → «1 час 30 мин».
 * Неизвестный или бессмысленный предел — null, тогда текст обходится без него.
 */
export function formatLimit(limitSec) {
  if (!limitSec || limitSec <= 0) return null;
  const totalMin = Math.round(limitSec / 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (!h) return `${m} ${plural(m, MINUTE_FORMS)}`;
  return m ? `${h} ${plural(h, HOUR_FORMS)} ${m} мин` : `${h} ${plural(h, HOUR_FORMS)}`;
}

/**
 * Предупреждение за WARN_BEFORE_SEC до предела; вне окна — null.
 *
 * Текст говорит и что случится, и что делать: человек в этот момент ведёт
 * встречу, разбираться ему некогда.
 *
 * @param {number|null} remainingSec — сколько осталось до предела.
 * @param {number|null} limitSec — сам предел (для человеческой подписи).
 */
export function capWarning(remainingSec, limitSec) {
  if (remainingSec === null || remainingSec === undefined) return null;
  if (remainingSec > WARN_BEFORE_SEC || remainingSec <= 0) return null;
  const min = Math.max(1, Math.ceil(remainingSec / 60));
  const limit = formatLimit(limitSec);
  return `Через ${min} ${plural(min, MINUTE_FORMS)} запись остановится сама`
    + (limit ? ` — это предел одной записи (${limit})` : '')
    + '. Сохраните её и начните новую, чтобы не потерять продолжение.';
}
